import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { ActivityIndicator, View, Text, StyleSheet, Image, RefreshControl } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { decode } from 'html-entities';

import Colors from '../constants/Colors';
import Languages from '../constants/Languages';
import Layout from '../constants/Layout';
import * as Actions from '../store/Actions';
import { TouchableOpacity, FlatList } from 'react-native-gesture-handler';
import timeUtils from '../utils/timeUtils';
import truncate from '../utils/truncate';
import UpdateThread from '../components/updates/UpdateThread';
import TopMenu from '../navigation/TopMenu';
import { getToken } from '../utils/token';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  view: {
    flex: 1,
    backgroundColor: Colors.lightBackground,
    flex: 1,
    alignItems: 'center',
  },
  mainContent: {
    flex: 1,
    maxWidth: 720,
    width: '100%',
  },
  cardView: {
    marginHorizontal: 0,
    marginVertical: 0,
    paddingHorizontal: 8,
    backgroundColor: Colors.lightBackground,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grayBorder,
  },
  header: {
    backgroundColor: Colors.white,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  headerTitle: {
    fontSize: 20,
    color: Colors.mediumBlue,
    fontWeight: '500',
  },
  updateView: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.white,
    width: '100%',
  },
  viewWithRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: Colors.white,
  },
  image: {
    width: 64,
    height: 64,
  },
  headingText: {
    fontSize: 17,
    // color:Colors.black
  },
  textStyle: {
    color: Colors.grayText,
  },
  emptyUpdateText: {
    fontSize: 17,
    color: Colors.grayText,
    paddingHorizontal: 20,
    textAlign: 'center',
  },
  emptyUpdateView: {
    paddingTop: 20,
    marginHorizontal: 20,
  },
  emptyViewImg: {
    marginBottom: 20,
    width: 128,
    height: 128,
    borderRadius: 128,
  },
  openCardImage: {
    width: 64,
    height: 64,
    position: 'absolute',
    top: -20,
    left: 10,
  },
  dateView: {
    position: 'absolute',
    right: 12,
    top: 8,
    color: Colors.grayText,
  },
  textContainer: {
    width: Layout.fullWidth - 80,
    maxWidth: 640,
    paddingHorizontal: 16,
  },
});

class UpdateScreen extends Component {
  static propTypes = {
    actions: PropTypes.objectOf(PropTypes.any).isRequired,
    pantry: PropTypes.objectOf(PropTypes.any).isRequired,
    registration: PropTypes.objectOf(PropTypes.any).isRequired,
    navigation: PropTypes.objectOf(PropTypes.any),
  };

  constructor(props) {
    super();
    this.state = {
      refreshing: false,
      updates: [],
      lastApiCall: 0,
      isLoading: true,
      isLoggedIn: false,
    };
  }

  spinnerTimer = null;

  componentDidMount = async () => {
    const { actions, navigation } = this.props;
    const token = await getToken();
    const isLoggedIn = Boolean(token);
    this.setState({ isLoggedIn });

    const now = new Date().getTime();
    this.setState({ lastApiCall: now });
    if (isLoggedIn) {
      this.spinnerTimer = setTimeout(() => {
        this.setState({ isLoading: false });
      }, 2000);
      actions.getUpdates();
    } else {
      this.setState({ isLoading: false });
    }
  };

  componentDidUpdate = async () => {
    const { pantry } = this.props;
    const { updates, lastApiCall } = pantry;
    if (updates && updates.length && lastApiCall > this.state.lastApiCall) {
      this.setState({ updates, lastApiCall });
    }
  };

  componentWillUnmount() {
    if (this.spinnerTimer) clearTimeout(this.spinnerTimer);
  }

  navigateToThread = (id) => {
    const { actions } = this.props;
    actions.selectThread(id);
  };

  onRefresh = () => {
    const { actions } = this.props;
    const { isLoggedIn } = this.state;
    if (isLoggedIn) {
      this.setState({ refreshing: true });
      actions.getUpdates(() => {
        this.setState({ refreshing: false });
      });
    }
  };

  capitalize = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  renderItem = ({ item }) => {
    const { registration } = this.props;
    const { language } = registration;
    const locale = language.name;
    const date = timeUtils.getShortDateFromUnixTimestamp(item.last_update.date, locale || 'en');
    const navigateToThread = () => {
      this.navigateToThread(item.id);
    };
    return (
      <TouchableOpacity style={[styles.cardView, styles.viewWithRow]} onPress={navigateToThread}>
        <View>
          {item.from_user && (
            <View
              style={{
                container: {
                  width: 20,
                  height: 20,
                  borderRadius: 10,
                  zIndex: 1,
                  bottom: -12,
                  right: -12,
                },
              }}
            >
              <Text>1</Text>
            </View>
          )}
          <Image style={styles.image} source={{ uri: item.avatar }} />
        </View>
        <Text style={styles.dateView}>{date}</Text>
        <View style={styles.textContainer}>
          <Text style={styles.headingText}>{truncate(item.pantry_name, 24)}</Text>
          <Text style={styles.textStyle}>{truncate(decode(item.last_update.description), 110)}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  render() {
    const { registration, pantry, actions, navigation } = this.props;
    const { refreshing, updates, isLoading } = this.state;
    const { language, user } = registration;
    const locale = language?.name || 'en';
    const translation = Languages.translations[locale];
    const labels = {
      noMessages: translation.please_sign_in_or_up,
    };
    if (user && user.id) {
      labels.noMessages = translation.fragment_messagethreadlist_textview_zero;
    }
    const { update_thread } = pantry;

    let content = null;
    if (update_thread && update_thread.id) {
      content = (
        <UpdateThread thread={update_thread} locale={locale} actions={actions} navigation={navigation} />
      );
    } else if (updates && updates.length) {
      content = (
        <View style={styles.view}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{this.capitalize(translation.updates)}</Text>
          </View>
          <FlatList
            data={updates}
            renderItem={this.renderItem}
            keyExtractor={(item) => `Update-${item.id}`}
            style={styles.mainContent}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={this.onRefresh} />}
          />
        </View>
      );
    } else {
      content = (
        <View style={styles.view}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>{this.capitalize(translation.updates)}</Text>
          </View>
          {isLoading ? (
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <ActivityIndicator size="large" color={Colors.mediumBlue} />
            </View>
          ) : (
            <View
              style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Image style={styles.emptyViewImg} source={require('../assets/images/updates.png')} />
              <View style={styles.emptyUpdateView}>
                <Text style={styles.emptyUpdateText}>{labels.noMessages}</Text>
              </View>
            </View>
          )}
        </View>
      );
    }

    const props = Object.assign({}, this.props, { page: 'updates' }, { translation });

    return (
      <SafeAreaView style={{ flex: 1 }} edges={['top', 'left', 'right']}>
        <View style={styles.container}>
          <TopMenu {...props} />
          {content}
        </View>
      </SafeAreaView>
    );
  }
}

function mapStateToProps(state) {
  return {
    registration: state.registration,
    pantry: state.pantry,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Actions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(UpdateScreen);
